import { state, computePermissions } from '../state.js';
import { dbClient } from '../db.js';
import { teamAccessLabel } from './roleLabels.js';

/** Load teams the user can access into state.teams */
export async function loadAccessibleTeams(userId = state.user?.id) {
  if (!userId) return [];

  const { data: teamsData, error: teamsError } = await dbClient
    .rpc('get_accessible_teams', { p_user_id: userId });

  let rawTeams = [];

  if (teamsError) {
    console.warn('get_accessible_teams error:', teamsError);
    const { data: fallbackTeams } = await dbClient
      .from('user_teams')
      .select('team_id, is_primary, access_level, teams:team_id(id, name, is_personal_team)')
      .eq('user_id', userId);

    if (fallbackTeams) {
      rawTeams = fallbackTeams.map(t => ({
        team_id: t.team_id,
        team_name: t.teams?.name || 'Unknown',
        is_primary: t.is_primary,
        access_level: t.access_level || 'member',
        is_personal_team: !!t.teams?.is_personal_team
      }));
    }
  } else {
    rawTeams = teamsData || [];
  }

  // FIN / FIH / etc. assigned via Role Assignments may have no user_teams row
  const roleTeams = await loadTeamsFromRoleAssignments(userId);
  rawTeams = [...rawTeams, ...roleTeams];

  // Fetch membership details from user_teams table for this user
  const { data: userTeamsList } = await dbClient
    .from('user_teams')
    .select('team_id, is_primary, access_level')
    .eq('user_id', userId);

  const membershipMap = {};
  if (userTeamsList) {
    userTeamsList.forEach(ut => {
      membershipMap[ut.team_id] = {
        is_primary: !!ut.is_primary,
        access_level: ut.access_level
      };
    });
  }

  const seenTeamIds = new Set();
  state.teams = [];
  for (const team of rawTeams) {
    if (team && team.team_id && !seenTeamIds.has(team.team_id)) {
      seenTeamIds.add(team.team_id);
      
      const explicit = membershipMap[team.team_id];
      if (explicit) {
        team.is_primary = explicit.is_primary;
        team.access_level = explicit.access_level || 'member';
      } else {
        team.is_primary = false;
        team.access_level = 'view';
        team.from_role_assignment = true;
      }
      
      state.teams.push(team);
    }
  }

  if (state.teams.length) {
    const teamIds = state.teams.map(t => t.team_id);
    const { data: teamMeta } = await dbClient
      .from('teams')
      .select('id, is_personal_team, has_budget_access, has_tasks_access, has_lms_access, gender_scope')
      .in('id', teamIds);

    const metaMap = Object.fromEntries((teamMeta || []).map(t => [t.id, t]));
    state.teams.forEach(t => {
      const meta = metaMap[t.team_id] || {};
      t.is_personal_team = !!meta.is_personal_team;
      t.has_budget_access = meta.has_budget_access !== false;
      t.has_tasks_access = meta.has_tasks_access !== false;
      t.has_lms_access = !!meta.has_lms_access;
      t.gender_scope = meta.gender_scope || 'mixed';
    });
  }

  return state.teams;
}

/** Teams granted only through request_role_assignments (approvers without membership). */
async function loadTeamsFromRoleAssignments(userId) {
  const userRole = state.user?.role;
  const isGlobalFromUserRole = ['caoh', 'oh', 'ceo', 'fih', 'fin', 'fip', 'cao'].includes(userRole);

  const { data: assignments, error } = await dbClient
    .from('request_role_assignments')
    .select('team_id, role_code')
    .eq('user_id', userId)
    .eq('is_active', true);

  const safeAssignments = assignments || [];
  if (error) {
    console.warn('role-assignment teams:', error.message);
  }

  if (!isGlobalFromUserRole && !safeAssignments.length) {
    return [];
  }

  const specificIds = [...new Set(safeAssignments.map(a => a.team_id).filter(Boolean))];
  const hasGlobal = isGlobalFromUserRole || safeAssignments.some(a => !a.team_id);

  let teamsQuery = dbClient
    .from('teams')
    .select('id, name, is_personal_team');

  // Prefer assigned teams; global FIN/FIH gets all non-personal teams
  if (!hasGlobal && specificIds.length) {
    teamsQuery = teamsQuery.in('id', specificIds);
  }

  const { data: teams, error: teamsErr } = await teamsQuery;
  if (teamsErr) {
    console.warn('role-assignment team list:', teamsErr.message);
    return [];
  }

  return (teams || [])
    .filter(t => hasGlobal ? !t.is_personal_team : true)
    .filter(t => hasGlobal || specificIds.includes(t.id))
    .map(t => ({
      team_id: t.id,
      team_name: t.name || 'Unknown',
      is_primary: false,
      access_level: 'view',
      is_personal_team: !!t.is_personal_team,
      from_role_assignment: true
    }));
}

/** Keep or restore current team after teams list changes. */
export function syncCurrentTeamAfterReload(preferredTeamId = state.currentTeam?.team_id) {
  if (!state.teams.length) return;

  const match = preferredTeamId
    ? state.teams.find(t => t.team_id === preferredTeamId)
    : null;

  state.currentTeam = match || state.teams.find(t => t.is_primary) || state.teams[0];
    if (state.currentTeam) {
    state.userTeamAccess = {
      access_level: String(state.currentTeam.access_level || 'member').toLowerCase().trim(),
      granted_by: state.currentTeam.granted_by,
      granted_at: state.currentTeam.granted_at
    };
  } else {
    state.userTeamAccess = { access_level: 'member' };
  }
  computePermissions();
}

export function populateTeamSwitcher() {
  const viewSelect = document.getElementById('viewModeSelect');
  const viewContainer = document.getElementById('viewSelectorContainer');
  
  if (viewSelect && state.user) {
    let allowed = state.user?.allowed_views || ['team'];
    if (typeof allowed === 'string') {
      try {
        const parsed = JSON.parse(allowed);
        if (Array.isArray(parsed)) allowed = parsed;
        else allowed = allowed.replace(/[{}]/g, '').replace(/"/g, '').split(',').map(s => s.trim());
      } catch (e) {
        allowed = allowed.replace(/[{}]/g, '').replace(/"/g, '').split(',').map(s => s.trim());
      }
    }
    let isAdmin = allowed.includes('admin');
    let isManager = allowed.includes('manager');
    
    // Emergency Fallback for Global Roles in case allowed_views parsing fails completely
    const role = String(state.user.role || '').toLowerCase();
    if (['admin', 'ceo', 'caoh', 'fih', 'oh'].includes(role)) { isAdmin = true; isManager = true; }
    if (['fin', 'fip', 'cao'].includes(role)) { isManager = true; }
    if (state.user.email === 'fih@k.com') {
      console.log('DEBUG FIH', { allowed, isAdmin, isManager });
      if (window.showToast) window.showToast('DEBUG: ' + JSON.stringify(allowed) + ' admin:' + isAdmin + ' mgr:' + isManager, 'info');
    }
    
    // Only show if user has options other than just Team
    if (isAdmin || isManager) {
      if (viewContainer) viewContainer.style.display = 'flex';
      
      let html = '<option value="team">Team</option>';
      if (isManager) html += '<option value="manager">Manager</option>';
      if (isAdmin) html += '<option value="admin">Admin</option>';
      
      viewSelect.innerHTML = html;
      
      if (!state.activeViewContext) {
        state.activeViewContext = state.user.default_login_view || 'team';
      }
      viewSelect.value = state.activeViewContext;
    } else {
      if (viewContainer) viewContainer.style.display = 'none';
      if (!state.activeViewContext) {
        state.activeViewContext = 'team';
      }
    }
  }

  const select = document.getElementById('teamSelect');
  if (!select || !state.currentTeam) return;
  select.innerHTML = '';

  state.teams.forEach(team => {
    const option = document.createElement('option');
    option.value = team.team_id;
    const rawName = team.team_name || '';
    const displayName = rawName.length > 15 ? rawName.slice(0, 15) + '...' : rawName;
    option.textContent = displayName + (team.is_primary ? ' ★' : '');
    if (team.team_id === state.currentTeam.team_id) {
      option.selected = true;
    }
    select.appendChild(option);
  });
}

export function updateAccessBadge() {
  const accessBadge = document.getElementById('userAccessLevel');
  if (accessBadge) {
    accessBadge.textContent = teamAccessLabel(state.userTeamAccess?.access_level);
  }
}

export async function refreshAccessibleTeams() {
  const preferredTeamId = state.currentTeam?.team_id;
  await loadAccessibleTeams();
  syncCurrentTeamAfterReload(preferredTeamId);
  if (state.user?.id && state.currentTeam?.team_id) {
    const { loadOkAccess } = await import('./okAccess.js');
    await loadOkAccess(state.user.id, state.currentTeam.team_id);
  }
  populateTeamSwitcher();
  updateAccessBadge();
  const { applyNavPermissions } = await import('./navPermissions.js');
  applyNavPermissions();
}
